import { useState, useEffect, useCallback } from 'react';
import { dashboardAPI, accountsAPI, bankingAPI, backupAPI, companyAPI } from '../../services/api';

/**
 * Aggregates the data the desktop dashboard needs from the accounting
 * backend: KPI summary, cash/bank account balances, and backup status.
 */
export default function useDashboardData(dateRange) {
  const [kpi, setKpi] = useState(null);
  const [balances, setBalances] = useState({ cash: 0, bank: 0, bankByCurrency: {}, baseCurrency: 'USD', rates: {} });
  const [backups, setBackups] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [salesTrend, setSalesTrend] = useState([]);
  const [expenseTrend, setExpenseTrend] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (dateRange?.startDate) params.startDate = dateRange.startDate;
      if (dateRange?.endDate) params.endDate = dateRange.endDate;
      const results = await Promise.allSettled([
        dashboardAPI.get(params),
        bankingAPI.accounts(),
        backupAPI.list(),
      ]);

      const kpiData = results[0].status === 'fulfilled' ? results[0].value : null;
      if (kpiData) {
        setKpi(kpiData);
        setRecentTransactions(kpiData.recent_transactions || []);
        setSalesTrend(kpiData.sales_trend || []);
        setExpenseTrend(kpiData.expense_trend || []);
        setTopCustomers(kpiData.top_customers || []);
        setTopProducts(kpiData.top_products || []);
      }

      // Cash from GL cash accounts; Bank from the actual bank register
      // (bank_accounts.bank_balance) so it always matches Banking page totals.
      // Bank balances are kept per account currency, so also group them by
      // currency and convert each into the company base currency.
      const bankAccts = results[1].status === 'fulfilled' ? results[1].value : [];
      let base = 'USD';
      let rateRows = [];
      try {
        const comp = await companyAPI.current();
        if (comp?.base_currency) base = comp.base_currency;
        const rrs = await companyAPI.exchangeRates();
        rateRows = Array.isArray(rrs) ? rrs : [];
      } catch (e) { /* keep defaults */ }
      const rateMap = {};
      rateRows.forEach(r => { rateMap[String(r.from_currency).toUpperCase()] = parseFloat(r.rate) || 1; });
      const bankByCurrency = {};
      let bankTotal = 0;
      for (const a of bankAccts) {
        const c = (a.currency && String(a.currency).toUpperCase()) || base;
        const amt = parseFloat(a.bank_balance || 0);
        bankByCurrency[c] = (bankByCurrency[c] || 0) + amt;
        bankTotal += amt * (c === base ? 1 : (rateMap[c] || 1));
      }

      let cashSum = kpiData?.cash || 0;
      try {
        const accounts = await accountsAPI.list({ active: 'true' });
        const cashAccts = accounts.filter(a => a.is_cash_account && !a.is_bank_account);
        if (cashAccts.length) {
          const settled = await Promise.allSettled(
            cashAccts.slice(0, 12).map(a => accountsAPI.balance(a.id))
          );
          cashSum = settled.reduce((sum, s) => sum + (s.status === 'fulfilled' ? parseFloat(s.value.balance || 0) : 0), 0);
        }
      } catch (e) { /* kpiData.cash fallback stays */ }
      setBalances({
        cash: cashSum,
        bank: Math.round(bankTotal * 100) / 100,
        bankByCurrency,
        baseCurrency: base,
        rates: rateMap,
      });

      if (results[2].status === 'fulfilled') setBackups(results[2].value || []);
    } catch (err) {
      console.error('Dashboard data error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, dateRange?.startDate, dateRange?.endDate]);

  const runBackup = useCallback(async () => {
    const res = await backupAPI.create({ type: 'Manual' });
    await load();
    return res;
  }, [load]);

  return { kpi, balances, backups, recentTransactions, salesTrend, expenseTrend, topCustomers, topProducts, loading, runBackup, refresh: load };
}
